import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { MapOverlaysControl } from "components/MapOverlaysControl/MapOverlaysControl";
import { config } from "config";
import { fitBoundsToGeojson, setSourceData } from "helpers/mapHelpers";
import mapboxgl, { Map } from "mapbox-gl";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useStore } from "store/StoreConfigs";
import { laneViewMapStyles } from "./LaneViewMapStyles";

export const LaneViewMap = observer(function LaneViewMap() {
    const classes = laneViewMapStyles();

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<Map>();
    const [mapLoaded, setMapLoaded] = useState(false);

    const controlContainer = document.createElement("div");
    controlContainer.className = "mapboxgl-ctrl";
    const controlRef = useRef(controlContainer);

    const {
        globalViewStore: { selectedLaneLineGeojson, selectedLanePointsGeojson },
        laneStore: { routesGeojson, loadingRoutes },
    } = useStore();

    useEffect(() => {
        if (map.current) {
            return;
        }

        map.current = new mapboxgl.Map({
            container: mapContainer.current!,
            ...config.map.initializationOptions,
            zoom: 1,
        });

        map.current.on("load", () => {
            map.current?.addSource("lanePoints", {
                type: "geojson",
            });

            map.current?.addSource("laneLine", {
                type: "geojson",
            });

            map.current?.addSource("routes", {
                type: "geojson",
            });

            map.current?.addSource("weather", {
                type: "raster",
                tiles: [
                    `https://maps.openweathermap.org/maps/2.0/weather/PA0/{z}/{x}/{y}?opacity=0.8&appid=${config.map.openWeatherMapKey}&palette=${config.map.accumulatedPrecipitationColorPalette}`,
                ],
                tileSize: 256,
            });

            map.current?.addLayer({
                id: "lanePoints",
                type: "circle",
                source: "lanePoints",
                paint: {
                    "circle-radius": 10,
                    "circle-color": ["string", ["get", "fillColor"]],
                    "circle-stroke-width": 2,
                    "circle-stroke-color": ["string", ["get", "strokeColor"]],
                },
            });

            map.current?.addLayer(
                {
                    id: "routes",
                    type: "line",
                    source: "routes",
                    layout: {
                        "line-cap": "round",
                    },
                    paint: {
                        "line-dasharray": [0.5, 2],
                        "line-width": 3,
                        "line-color": ["string", ["get", "color"]],
                        "line-opacity": 1,
                    },
                },
                "lanePoints"
            );

            map.current?.addLayer(
                {
                    id: "laneLine",
                    type: "line",
                    source: "laneLine",
                    layout: {
                        "line-cap": "round",
                    },
                    paint: {
                        "line-width": 10,
                        "line-color": ["string", ["get", "color"]],
                        "line-opacity": 1,
                    },
                },
                "routes"
            );

            map.current?.addLayer(
                {
                    id: "weather",
                    type: "raster",
                    source: "weather",
                    layout: {
                        visibility: "none",
                    },
                },
                "routes"
            );

            map.current?.addControl({
                onAdd() {
                    return controlRef.current;
                },

                onRemove() {},
            });

            setMapLoaded(true);
        });

        return () => {
            map.current?.remove();
        };
    }, []);

    useEffect(() => {
        if (mapLoaded) {
            setSourceData(selectedLaneLineGeojson, "laneLine", map.current);
            fitBoundsToGeojson(selectedLaneLineGeojson, map.current);
        }
    }, [selectedLaneLineGeojson, mapLoaded]);

    useEffect(() => {
        if (mapLoaded) {
            setSourceData(selectedLanePointsGeojson, "lanePoints", map.current);
        }
    }, [selectedLanePointsGeojson, mapLoaded]);

    useEffect(() => {
        if (mapLoaded) {
            setSourceData(routesGeojson, "routes", map.current);
        }
    }, [routesGeojson, mapLoaded]);

    const overlays = [
        {
            name: "weather",
            displayName: "Weather",
        },
        {
            name: "portCongestion",
            displayName: "Port Congestion",
        },
        {
            name: "dwellTime",
            displayName: "Dwell Time",
        },
        {
            name: "routes",
            displayName: "Routes",
        },
    ];

    const [selectedOverlays, setSelectedOverlays] = useState<string[]>([
        "routes",
    ]);

    useEffect(() => {
        overlays.forEach((overlay) => {
            if (map.current?.getLayer(overlay.name)) {
                map.current?.setLayoutProperty(
                    overlay.name,
                    "visibility",
                    selectedOverlays.includes(overlay.name) ? "visible" : "none"
                );
            }
        });
    }, [selectedOverlays]);

    return (
        <div ref={mapContainer} className={classes.container}>
            {loadingRoutes && selectedOverlays.includes("routes") && (
                <div className={classes.indicatorContainer}>
                    <LoadingIndicator className={classes.indicator} isDimmed />
                </div>
            )}
            {ReactDOM.createPortal(
                <MapOverlaysControl
                    overlays={overlays}
                    selected={selectedOverlays}
                    onChange={(selected) => setSelectedOverlays(selected)}
                />,
                controlRef.current
            )}
        </div>
    );
});
